import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Shared modal a11y: focus-on-open (to initialFocusRef, optionally falling back to the
 *  dialog container), focus-restore on close/unmount, Escape-to-close (skipped when
 *  !canClose), Tab focus-trap (focusables recomputed live each keydown). Returns the
 *  dialogRef to spread onto the dialog container.
 *
 *  `active` gates both effects so callers that mount the dialog content unconditionally
 *  (open-prop controlled) get the same timing as their old `if (!open) return` effects;
 *  callers that mount the content only when open can leave it at the default `true`. */
export function useModalDialog(opts: {
  onClose: () => void;
  canClose: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  active?: boolean;
  fallbackToDialog?: boolean;
}): { dialogRef: RefObject<HTMLDivElement | null> } {
  const { onClose, canClose, initialFocusRef, active = true, fallbackToDialog = false } = opts;
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const handle = requestAnimationFrame(() => {
      const target = initialFocusRef?.current ?? (fallbackToDialog ? dialogRef.current : null);
      target?.focus();
    });

    return () => {
      cancelAnimationFrame(handle);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canClose) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, canClose, onClose]);

  return { dialogRef };
}
